import React, { useState, useEffect } from 'react';

const PWAUpdateManager = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [hasReloaded, setHasReloaded] = useState(false);

  useEffect(() => {
    // Check if service worker is supported
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);
        
        // Listen for service worker updates
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker is installed and waiting
                setUpdateAvailable(true);
                console.log('🔄 New PWA update available!');
                
                // Auto-update after 5 seconds if user doesn't interact
                setTimeout(() => {
                  if (updateAvailable) {
                    console.log('🔄 Auto-updating PWA...');
                    handleUpdate();
                  }
                }, 5000);
              }
            });
          }
        });

        // Check if there's already a waiting service worker
        if (reg.waiting) {
          setUpdateAvailable(true);
          console.log('🔄 PWA update already waiting!');
          
          // Auto-update after 3 seconds
          setTimeout(() => {
            console.log('🔄 Auto-updating PWA...');
            handleUpdate();
          }, 3000);
        }
        
        // Check for updates every 30 seconds
        setInterval(() => {
          reg.update();
        }, 30000);
      });

      // Listen for controller change (when update is applied)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('✅ PWA update applied successfully!');
        setUpdateAvailable(false);
        setIsUpdating(false);
        // Only reload once to prevent infinite loops
        if (!hasReloaded && isUpdating) {
          console.log('🔄 Reloading page after PWA update...');
          setHasReloaded(true);
          setTimeout(() => {
            window.location.reload();
          }, 2000); // Longer delay to ensure state is updated
        }
      });
    }
  }, []);

  const handleUpdate = async () => {
    if (!registration || !registration.waiting) return;

    setIsUpdating(true);
    
    try {
      // Tell the waiting service worker to skip waiting and become active
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // The controllerchange event will handle the reload
    } catch (error) {
      console.error('Error applying PWA update:', error);
      setIsUpdating(false);
    }
  };

  const dismissUpdate = () => {
    setUpdateAvailable(false);
  };

  if (!updateAvailable) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 animate-slide-down">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              App Update Available
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              A new version of the app is ready. Update now to get the latest features and improvements.
            </p>
            
            <div className="flex gap-2">
              <button
                onClick={handleUpdate}
                disabled={isUpdating}
                className="flex-1 bg-blue-600 text-white text-xs font-medium py-2 px-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUpdating ? (
                  <div className="flex items-center justify-center gap-1">
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating...
                  </div>
                ) : (
                  'Update Now'
                )}
              </button>
              
              <button
                onClick={dismissUpdate}
                disabled={isUpdating}
                className="px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Later
              </button>
            </div>
          </div>
          
          <button
            onClick={dismissUpdate}
            disabled={isUpdating}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAUpdateManager;

